import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["pillsContainer", "operatorNode", "saveButton", "template", "emptyState"]
  static values = { saveUrl: String, operator: { type: String, default: "and" } }

  connect() {
    console.log(`[filter-builder] connect: saveUrl=${this.saveUrlValue} operator=${this.operatorValue}`)
    this._baselineData = this._serialize()
  }

  toggleOperator() {
    this.operatorValue = this.operatorValue === "and" ? "or" : "and"
    this.operatorNodeTargets.forEach(node => {
      node.textContent = this.operatorValue.toUpperCase()
    })
    this._checkDirty()
  }

  addCondition() {
    const container = this.pillsContainerTarget
    const addBtn = container.querySelector("[data-add-btn]")
    const existingPills = container.querySelectorAll("[data-condition-id]")

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("hidden")
    }

    if (existingPills.length > 0) {
      addBtn.before(this._createConnector())
    }

    const clone = this.templateTarget.content.cloneNode(true)
    clone.querySelector("[data-condition-id]").dataset.conditionId = `new_${Date.now()}`
    addBtn.before(clone)
    this._checkDirty()
  }

  removeCondition(event) {
    const pill = event.target.closest("[data-condition-id]")
    if (!pill) return

    const prev = pill.previousElementSibling
    const next = pill.nextElementSibling

    if (prev && prev.hasAttribute("data-connector")) prev.remove()
    else if (next && next.hasAttribute("data-connector")) next.remove()

    pill.remove()

    const remaining = this.pillsContainerTarget.querySelectorAll("[data-condition-id]")
    if (remaining.length === 0 && this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.remove("hidden")
    }
    this._checkDirty()
  }

  typeChanged(event) {
    const pill = event.target.closest("[data-condition-id]")
    const isNumeric = ["engagement_rate", "reach", "views", "impressions", "shares", "video_views"].includes(event.target.value)
    console.log(`[filter-builder] typeChanged: type=${event.target.value} isNumeric=${isNumeric}`)
    const textEl = pill.querySelector("[data-text-inputs]")
    const numEl = pill.querySelector("[data-numeric-inputs]")
    if (textEl) textEl.classList.toggle("hidden", isNumeric)
    if (numEl) numEl.classList.toggle("hidden", !isNumeric)
    this._checkDirty()
  }

  inputChanged() {
    this._checkDirty()
  }

  async save() {
    const pills = this.pillsContainerTarget.querySelectorAll("[data-condition-id]")
    if (!pills.length && !this._baselineData) return

    const formData = new FormData()
    formData.append("_method", "PATCH")
    formData.append("condition_type", this.operatorValue)

    pills.forEach((pill, i) => {
      pill.querySelectorAll("[name]").forEach(input => {
        if (input.closest(".hidden")) return
        const field = input.name.match(/\[(\w+)\]$/)?.[1]
        if (field) formData.append(`conditions[${i}][${field}]`, input.value)
      })
    })

    const btn = this.saveButtonTarget
    btn.classList.add("opacity-50", "pointer-events-none")

    const resp = await fetch(this.saveUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        "Accept": "application/json"
      },
      body: formData
    })

    if (resp.ok) {
      btn.classList.add("hidden")
      btn.classList.remove("inline-flex", "opacity-50", "pointer-events-none")
      this._baselineData = this._serialize()
    } else {
      btn.classList.remove("opacity-50", "pointer-events-none")
    }
  }

  _serialize() {
    const pills = this.pillsContainerTarget.querySelectorAll("[data-condition-id]")
    const conditions = Array.from(pills).map(pill => {
      const obj = {}
      pill.querySelectorAll("[name]").forEach(input => {
        if (input.closest(".hidden")) return
        const field = input.name.match(/\[(\w+)\]$/)?.[1]
        if (field) obj[field] = input.value
      })
      return obj
    })
    return JSON.stringify({ operator: this.operatorValue, conditions })
  }

  _checkDirty() {
    const dirty = this._serialize() !== this._baselineData
    const btn = this.saveButtonTarget
    if (dirty) {
      btn.classList.remove("hidden")
      btn.classList.add("inline-flex")
    } else {
      btn.classList.add("hidden")
      btn.classList.remove("inline-flex")
    }
  }

  _createConnector() {
    const span = document.createElement("span")
    span.className = "inline-flex items-center mx-px"
    span.setAttribute("data-connector", "")
    const label = this.operatorValue.toUpperCase()
    span.innerHTML = [
      '<span class="w-1.5 h-px bg-gray-300 dark:bg-gray-600"></span>',
      `<button type="button" data-filter-builder-target="operatorNode" data-action="click->filter-builder#toggleOperator" class="text-[8px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full px-1 leading-3.5 cursor-pointer hover:text-accent hover:border-accent transition-colors">${label}</button>`,
      '<span class="w-1.5 h-px bg-gray-300 dark:bg-gray-600"></span>'
    ].join("")
    return span
  }
}
